// b20: appen märker en ny version efter en driftsättning. Förut visade en öppen app, och en app som
// laddades om på vanligt sätt, den gamla versionen: /app.js och /app.css hade fasta namn och inget
// Cache-Control, så webbläsaren gissade själv hur länge de var färska, och tjänstearbetarens
// "nätet först" hämtade dem ur samma cache. Bara Ctrl+Shift+R gick förbi.
//
// Nu:
//   - bygg.ts skriver appens version (en hash av app.js och app.css) i app.js och i /version.json,
//     och index.html hämtar /app.js?v=<version>, så att en ny version har nya adresser;
//   - molnet ger alla appens filer Cache-Control: no-cache, och /version.json no-store;
//   - appen frågar efter /version.json när den startar, var femte minut, när fönstret får fokus och
//     när den blir synlig igen. Är versionen en annan visas "En ny version av appen finns – Ladda
//     om". Har appen legat i bakgrunden laddar den om av sig själv när den blir synlig igen.

/** Appens version. bygg.ts byter texten mot versionen, lika lång, så att källkartan stämmer. */
export const APPVERSION: string = "__APPVERSION__";

/** Så ofta appen frågar efter versionen medan den visas. */
export const VERSION_MS = 5 * 60_000;

const NYCKEL = "batkoll-laddade-om-till";

/** Versionen som molnet har nu, eller null om den inte går att läsa. */
export async function publiceradVersion(): Promise<string | null> {
  try {
    const svar = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!svar.ok) return null;
    const v = ((await svar.json()) as { version?: unknown }).version;
    return typeof v === "string" && /^[0-9a-f_A-Z]{6,40}$/.test(v) ? v : null;
  } catch {
    return null;
  }
}

/** Har appen redan laddat om för den här versionen, utan att få den? Då visas rutan i stället. */
function redanForsokt(version: string): boolean {
  try {
    return sessionStorage.getItem(NYCKEL) === version;
  } catch {
    return false;
  }
}

/** Laddar om appen till den nya versionen, med tjänstearbetaren uppdaterad först. */
export async function laddaOm(version: string | null): Promise<void> {
  try {
    if (version) sessionStorage.setItem(NYCKEL, version);
  } catch {}
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    await Promise.race([reg?.update(), new Promise((r) => setTimeout(r, 2000))]);
  } catch {}
  location.reload();
}

/**
 * Följer versionen. `ny` anropas med den nya versionen när appen visas, och appen laddar om av sig
 * själv när den blir synlig efter att ha legat i bakgrunden. Ger en funktion som slutar följa.
 */
export function foljVersionen(ny: (version: string) => void): () => void {
  let dold = document.visibilityState === "hidden";
  const kolla = async (franBakgrunden: boolean) => {
    const v = await publiceradVersion();
    if (!v || v === APPVERSION) return;
    if (franBakgrunden && !redanForsokt(v)) await laddaOm(v);
    else ny(v);
  };
  const synlig = () => {
    if (document.visibilityState === "hidden") {
      dold = true;
      return;
    }
    const franBakgrunden = dold;
    dold = false;
    void kolla(franBakgrunden);
  };
  const fokus = () => void kolla(false);
  void kolla(false);
  const t = setInterval(() => {
    if (document.visibilityState === "visible") void kolla(false);
  }, VERSION_MS);
  document.addEventListener("visibilitychange", synlig);
  addEventListener("focus", fokus);
  return () => {
    clearInterval(t);
    document.removeEventListener("visibilitychange", synlig);
    removeEventListener("focus", fokus);
  };
}
